import * as XLSX from 'xlsx';
import { DentalCallType, DentalListDefinition, DentalListField, DentalListRow, DentalProcedureCodeMatch } from '../../../shared/models/outbound/DentalPatientList';
import { PmsProcedureTypeModel } from '../../../shared/models/organizations/PmsLookupModels';

const patientFields: DentalListField[] = [
    { key: 'patientId', label: 'Patient ID', hint: 'The patient ID from Denticon. Format this column as text.', example: '104582', required: true },
    { key: 'phoneNumber', label: 'Phone number', hint: 'US or Canadian number, with or without +1. Formatting is accepted.', example: '+14165550123', required: true },
    { key: 'officeId', label: 'Office ID', hint: 'The Denticon office ID for this patient, not the office name. Leave the column out to use this app’s outbound office.', example: '266', required: false, defaultsFromApp: true }
];
const appointmentFields: DentalListField[] = [
    ...patientFields,
    { key: 'appointmentId', label: 'Appointment ID', hint: 'Identifies the specific appointment in Denticon.', example: '780123', required: true },
    { key: 'appointmentDate', label: 'Appointment date', hint: 'Use YYYY-MM-DD. This is the appointment date, not the call date.', example: '2026-11-20', required: true }
];

// Proposed spreadsheet contract; patient names, DOB, status, integration metadata and
// call-reason payloads must be resolved by the future import endpoint, never trusted from a file.
export const dentalListDefinitions: DentalListDefinition[] = [
    { type: 'outbound-reactivation', title: 'Reactivation', description: 'Bring inactive patients back to the practice.', check: 'Before calling, check Denticon for a newly booked appointment and skip patients who have booked.', fields: [
        patientFields[0], patientFields[2], { ...patientFields[1], label: 'Patient phone number' },
        { key: 'lastCleaningDate', label: 'Last cleaning date', hint: 'Optional. Use MM/DD/YYYY or YYYY-MM-DD for the patient’s last cleaning.', example: '2025-05-12', required: false },
        { key: 'productionTypeId', label: 'Production type ID', hint: 'The Denticon production type to book. It must be bookable online. Leave blank to use a procedure code instead.', example: '102', required: false },
        { key: 'procedureCode', label: 'Procedure code', hint: 'Use instead of a production type ID, for example D1110. One code per row. You’ll match each code to a production type after upload.', example: 'D1110', required: false }
    ], requireOneOf: ['productionTypeId', 'procedureCode'], booksProductionType: true },
    { type: 'outbound-missed-appointment', title: 'Missed appointments', description: 'Help patients rebook after a missed visit.', check: 'Before calling, check for a replacement or later appointment and skip patients who have already rebooked.', fields: appointmentFields },
    { type: 'outbound-appointment-confirmation', title: 'Unconfirmed appointments', description: 'Ask patients to confirm an upcoming visit.', check: 'Use the existing confirmation check before calling. Skip appointments that are already confirmed, cancelled, or no longer eligible.', fields: appointmentFields },
    { type: 'outbound-appointment-reminder', title: 'Appointment reminders', description: 'Remind confirmed patients about their visit.', check: 'Before calling, verify that the appointment is still upcoming and confirmed.', fields: appointmentFields },
    { type: 'outbound-recall', title: 'Recalls', description: 'Reach patients due for routine follow-up care.', check: 'Before calling, refresh recall eligibility in Denticon and check whether the required care has already been booked.', fields: [
        ...patientFields,
        { key: 'procedureCode', label: 'Procedure code', hint: 'The Denticon recall procedure code, for example D0120. One code per row. You’ll match each code to a production type after upload.', example: 'D0120', required: true },
        { key: 'recallDate', label: 'Recall due date', hint: 'Use YYYY-MM-DD for the recall due date.', example: '2026-11-20', required: true },
        { key: 'recallReason', label: 'Recall reason', hint: 'Optional context for the recall.', example: 'Periodic exam', required: false },
        { key: 'productionTypeId', label: 'Production type ID', hint: 'Optional. The Denticon production type to book; it must be bookable online. Leave blank to use the procedure code’s match.', example: '102', required: false }
    ], booksProductionType: true }
];

export const MAX_LIST_ROWS = 20000;
export const MAX_FILE_BYTES = 10 * 1024 * 1024;
export type ColumnMapping = Record<string, string>;

const headerKey = (value: string) => value.toLowerCase().replace(/\([^)]*\)/g, '').replace(/[^a-z0-9]/g, '');
export function suggestMapping(headers: string[], definition: DentalListDefinition): ColumnMapping {
    const aliases: Record<string, string[]> = {
        patientId: ['patientid', 'patid'], phoneNumber: ['phonenumber', 'patientphonenumber', 'phone', 'cellphone', 'mobile', 'homephone'],
        officeId: ['officeid', 'oid'], appointmentId: ['appointmentid', 'apptid'],
        appointmentDate: ['appointmentdate', 'apptdate'], recallDate: ['recalldate', 'recallduedate'],
        procedureCode: ['procedurecode', 'recallprocedurecode', 'recallcode', 'proccode', 'cdtcode', 'adacode'], recallReason: ['recallreason'],
        lastCleaningDate: ['lastcleaningdate', 'lastcleaning', 'lastcleaningappointmentdate'], productionTypeId: ['productiontypeid', 'productiontype']
    };
    return definition.fields.reduce((mapping, field) => {
        const index = headers.findIndex(header => (aliases[field.key] || [field.key]).includes(headerKey(header)));
        mapping[field.key] = index < 0 ? '' : String(index);
        return mapping;
    }, {} as ColumnMapping);
}

export function normalizePhone(value: string): string {
    if (!/^[+\d\s().-]+$/.test(value) || (value.includes('+') && value.indexOf('+') !== 0)) return '';
    const digits = value.replace(/\D/g, '');
    const national = digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits;
    return /^[2-9]\d{2}[2-9]\d{6}$/.test(national) ? `+1${national}` : '';
}

function validDate(value: string): boolean {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    const date = new Date(`${value}T00:00:00Z`);
    return !isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

export function normalizeDentalDate(value: string): string {
    if (validDate(value)) return value;
    const parts = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value);
    if (!parts) return '';
    const iso = `${parts[3]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
    return validDate(iso) ? iso : '';
}

export function rowIdentity(type: DentalCallType, values: Record<string, string>): string {
    // Shared household phones must not collapse distinct patients or appointments.
    // A reactivation procedure code only picks the production type; it is not part of the patient's identity.
    return JSON.stringify([type, values.officeId, values.patientId,
        values.appointmentId || '', type === 'outbound-reactivation' ? '' : values.procedureCode || '', values.recallDate || '']);
}

// appDefaults fills fields marked defaultsFromApp whose column isn't matched.
export function validateDentalRows(data: string[][], mapping: ColumnMapping, definition: DentalListDefinition, firstDataRow = 2, appDefaults: Record<string, string> = {}): DentalListRow[] {
    const seen = new Set<string>();
    // Saved-call duplicates are checked by the API against current pending calls.
    return data.map((cells, index) => ({ cells, rowNumber: index + firstDataRow }))
        .filter(row => row.cells.some(cell => String(cell).trim() !== ''))
        .map(({ cells, rowNumber }) => {
            const values: Record<string, string> = {};
            const errors: string[] = [];
            definition.fields.forEach(field => {
                const column = mapping[field.key];
                const unmatched = column === '' || column === undefined;
                const value = unmatched ? (field.defaultsFromApp && appDefaults[field.key]) || '' : String(cells[Number(column)] || '').trim();
                values[field.key] = value;
                if (!value && (field.required || field.defaultsFromApp)) errors.push(`${field.label} is required`);
                if (value && /Id$/.test(field.key) && (!/^\d+$/.test(value) || !/[1-9]/.test(value))) errors.push(`${field.label} must be a positive Denticon ID; use text cells without decimals`);
                if (value && field.key === 'phoneNumber') {
                    const phone = normalizePhone(value);
                    if (!phone) errors.push('Enter a valid US or Canadian phone number');
                    else values[field.key] = phone;
                }
                if (value && field.key === 'procedureCode') {
                    if (/[\s,;]/.test(value)) errors.push('Use one procedure code per row');
                    else values[field.key] = value.toUpperCase();
                }
                if (value && /Date$/.test(field.key)) {
                    const date = normalizeDentalDate(value);
                    if (!date) errors.push(`${field.label} must be a real date in YYYY-MM-DD or MM/DD/YYYY format`);
                    else values[field.key] = date;
                }
            });
            if (definition.requireOneOf && definition.requireOneOf.every(key => !values[key])) {
                errors.push(`${definition.requireOneOf.map(key => definition.fields.find(field => field.key === key)!.label).join(' or ')} is required`);
            }
            const identity = rowIdentity(definition.type, values);
            const duplicate = !errors.length && seen.has(identity);
            if (!errors.length) seen.add(identity);
            return { rowNumber, values, errors, duplicate };
        });
}

export function readDentalSheet(workbook: XLSX.WorkBook, sheetName: string): { headers: string[]; data: string[][]; firstDataRow: number } {
    const sheet = workbook.Sheets[sheetName];
    if (!sheet || !sheet['!ref']) throw new Error('This worksheet is empty. Choose another worksheet.');
    const range = XLSX.utils.decode_range(sheet['!fullref'] || sheet['!ref']);
    if (range.e.c > 99) throw new Error('Use at most 100 columns. Remove unused formatted columns.');
    const rows = XLSX.utils.sheet_to_json<string[]>(sheet, { header: 1, raw: false, defval: '', blankrows: true, dateNF: 'yyyy-mm-dd', range: 0 });
    // Excel date cells can retain a localized display format even with dateNF set.
    Object.keys(sheet).filter(address => address[0] !== '!').forEach(address => {
        const cell = sheet[address];
        const position = XLSX.utils.decode_cell(address);
        if (cell.t === 'n' && Math.abs(cell.v) > Number.MAX_SAFE_INTEGER) throw new Error('A numeric cell exceeds Excel’s safe ID precision. Export IDs as text and upload again.');
        if (cell.t === 'd' && cell.v instanceof Date && rows[position.r]) {
            const date = cell.v as Date;
            rows[position.r][position.c] = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        }
    });
    const titleRow = (rows[0] || []).filter(value => String(value).trim());
    const headerIndex = titleRow.length === 1 && /^Voicify Outbound Call:/i.test(String(titleRow[0]).trim()) &&
        (rows[1] || []).some(value => headerKey(String(value)) === 'patientid') ? 1 : 0;
    if (range.e.r - headerIndex > MAX_LIST_ROWS) throw new Error('Use at most 20,000 patient rows. Remove unused formatted rows.');
    const headers = (rows[headerIndex] || []).map(value => String(value).trim());
    if (!headers.some(Boolean)) throw new Error('Add column headers in the first row, or directly below the Voicify title.');
    if (!rows.slice(headerIndex + 1).some(row => row.some(value => String(value).trim()))) throw new Error('This worksheet has headers but no patient rows. Add patients and upload it again.');
    return { headers, data: rows.slice(headerIndex + 1), firstDataRow: headerIndex + 2 };
}

// Recall Appointment ProductionType Mapping, shared with recalls: { "<productionTypeId>": ["D1110", ...] }.
// Recall booking needs each procedure code under exactly one production type.
export type ProductionTypeMapping = Record<string, string[]>;

export function parseProductionTypeMapping(value: string): ProductionTypeMapping {
    if (!value || !value.trim()) return {};
    let parsed: any;
    try { parsed = JSON.parse(value); } catch { parsed = undefined; }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed) ||
        Object.keys(parsed).some(key => !Array.isArray(parsed[key]) || parsed[key].some((code: any) => typeof code !== 'string'))) {
        throw new Error('The saved Recall Appointment ProductionType Mapping is not valid JSON in the form {"12": ["D1110"]}. Fix it in the outbound settings, then try again.');
    }
    return parsed;
}

const sameCode = (first: string, second: string) => first.trim().toUpperCase() === second.trim().toUpperCase();

export function savedProductionTypeIds(mapping: ProductionTypeMapping, code: string): string[] {
    return Object.keys(mapping).filter(id => mapping[id].some(item => sameCode(item, code)));
}

// Codes come from valid rows that book by procedure code. Duplicates count too: one may end up representing the patient.
export function procedureCodeMatches(rows: DentalListRow[], mapping: ProductionTypeMapping, bookable: PmsProcedureTypeModel[]): DentalProcedureCodeMatch[] {
    const counts = rows.filter(row => !row.errors.length && !row.values.productionTypeId && row.values.procedureCode)
        .reduce((codes, row) => ({ ...codes, [row.values.procedureCode]: (codes[row.values.procedureCode] || 0) + 1 }), {} as Record<string, number>);
    return Object.keys(counts).sort().map(code => {
        const saved = savedProductionTypeIds(mapping, code);
        if (!saved.length) return { code, rowCount: counts[code], productionTypeId: '', locked: false };
        const issue = saved.length > 1 ? `Procedure code ${code} is saved under more than one production type (${saved.join(', ')}) in the Recall Appointment ProductionType Mapping`
            : !bookable.some(type => type.id === saved[0]) ? `Procedure code ${code} is saved to production type ${saved[0]}, which is not bookable online` : undefined;
        return { code, rowCount: counts[code], productionTypeId: saved.length === 1 ? saved[0] : '', locked: true, issue };
    });
}

function resolveProductionType(row: DentalListRow, matches: DentalProcedureCodeMatch[], bookable: PmsProcedureTypeModel[]): DentalListRow {
    const match = !row.values.productionTypeId && matches.find(item => item.code === row.values.procedureCode);
    if (match && match.issue) return { ...row, errors: [match.issue] };
    const productionTypeId = row.values.productionTypeId || (match ? match.productionTypeId : '');
    if (!productionTypeId) return { ...row, errors: [`Match procedure code ${row.values.procedureCode} to a production type`] };
    const values = { ...row.values, productionTypeId };
    return bookable.some(type => type.id === productionTypeId) ? { ...row, values }
        : { ...row, values, errors: [`Production type ${productionTypeId} is not bookable online in Denticon. Use a production type that is bookable online.`] };
}

// Returns new rows with production types filled from the matches and rows that can't be booked online flagged.
// Duplicates are decided again afterwards, so a bookable copy of a patient represents them when an earlier copy can't be booked.
export function applyProductionTypes(rows: DentalListRow[], matches: DentalProcedureCodeMatch[], bookable: PmsProcedureTypeModel[], type: DentalCallType = 'outbound-reactivation'): DentalListRow[] {
    const resolved = rows.map(row => row.errors.length ? row : resolveProductionType({ ...row, duplicate: false }, matches, bookable));
    const representatives = new Map<string, number>();
    resolved.forEach((row, index) => {
        const identity = rowIdentity(type, row.values);
        if (!rows[index].errors.length && !row.errors.length && !representatives.has(identity)) representatives.set(identity, index);
    });
    return resolved.map((row, index) => {
        if (rows[index].errors.length) return row;
        const representative = representatives.get(rowIdentity(type, row.values));
        return representative === undefined || representative === index ? row : { ...row, errors: [], duplicate: true };
    });
}

// Keep or add only: a code already saved under any production type keeps it.
export function mergeProductionTypeMapping(mapping: ProductionTypeMapping, matches: DentalProcedureCodeMatch[]): ProductionTypeMapping {
    return matches.filter(match => !match.locked && match.productionTypeId).reduce((merged, match) => {
        if (Object.keys(merged).some(id => merged[id].some(code => sameCode(code, match.code)))) return merged;
        return { ...merged, [match.productionTypeId]: [...(merged[match.productionTypeId] || []), match.code] };
    }, mapping);
}

export function dentalTemplateRows(definition: DentalListDefinition): string[][] {
    return definition.type === 'outbound-reactivation' ? [
        ['Voicify Outbound Call: Reactivation (Patient overdue within 6 months)', '', '', '', '', ''],
        ['Patient ID', 'OID', 'Patient Phone Number', 'Last Cleaning Date (MM/DD/YYYY)', 'Production Type Id', 'Procedure Code']
    ] : [definition.fields.map(field => field.key)];
}

export function downloadDentalTemplate(definition: DentalListDefinition) {
    const workbook = XLSX.utils.book_new();
    const sheet = XLSX.utils.aoa_to_sheet(dentalTemplateRows(definition));
    sheet['!cols'] = definition.fields.map(field => ({ wch: field.key === 'lastCleaningDate' ? 34 : 24 }));
    if (definition.type === 'outbound-reactivation') sheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }];
    XLSX.utils.book_append_sheet(workbook, sheet, 'Patients');
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet([
        ['Field', 'Required', 'Instructions', 'Example (do not import)'],
        ...definition.fields.map(field => [field.key, field.required ? 'Yes' : 'No', field.hint, field.example])
    ]), 'Instructions');
    XLSX.writeFile(workbook, `${definition.type.replace('outbound-', '')}-template.xlsx`);
}

export function downloadDentalIssues(rows: DentalListRow[], definition: DentalListDefinition) {
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet([
        ['Source row', ...definition.fields.map(field => field.key), 'Issues'],
        ...rows.filter(row => row.errors.length || row.duplicate).map(row => [row.rowNumber,
            ...definition.fields.map(field => row.values[field.key]), row.duplicate ? 'Duplicate patient / appointment' : row.errors.join('; ')])
    ]), 'Issues');
    XLSX.writeFile(workbook, 'patient-list-issues.xlsx');
}
